import { Link } from 'react-router-dom';
import { useMemo, useState } from 'react';
import { useQuery } from 'urql';
import EmptyState from '@/components/common/EmptyState.jsx';
import PageHeaderCompact from '@/components/common/PageHeaderCompact.jsx';
import { useAuth } from '@/state/auth/AuthContext.jsx';
import { ESTADISTICAS_REPARTOS } from '@/graphql/repartos.gql.js';
import {
  defaultStatsRange,
  efectividad,
  formatCurrency,
  formatPercent,
  pendientesDe,
  summarizeRepartos,
} from './repartosStats.js';

const ESTADO_LABELS = {
  borrador: 'Borrador',
  abierto: 'Abierto',
  en_curso: 'En curso',
  cerrado: 'Cerrado',
  rendicion_parcial: 'Rendición parcial',
  rendido: 'Rendido',
  liquidado: 'Liquidado',
};

const inputClassName =
  'mt-1.5 w-full rounded-xl border border-[rgb(var(--border))] bg-[rgb(var(--subpanel))] px-3 py-2 text-sm text-[rgb(var(--fg))] focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))]';
const labelClassName = 'text-[11px] font-semibold uppercase tracking-[0.12em] text-[rgb(var(--muted))]';

function KpiCard({ label, value, hint }) {
  return (
    <article className="rounded-2xl border border-[rgb(var(--border))] bg-[rgb(var(--panel))] px-3 py-2">
      <p className="text-[10px] uppercase tracking-[0.12em] text-[rgb(var(--muted))]">{label}</p>
      <p className="mt-0.5 text-xl font-semibold text-[rgb(var(--fg))]">{value}</p>
      {hint ? <p className="text-[11px] text-[rgb(var(--muted))]">{hint}</p> : null}
    </article>
  );
}

export default function RepartosEstadisticas() {
  const { sucursal } = useAuth();
  const sucursalId = Number(sucursal?.id || 0);
  const [{ desde, hasta }, setRange] = useState(() => defaultStatsRange());

  const [statsQ] = useQuery({
    query: ESTADISTICAS_REPARTOS,
    variables: { sucursalId, fechaDesde: desde || null, fechaHasta: hasta || null },
    pause: !sucursalId,
    requestPolicy: 'network-only',
  });

  const rows = useMemo(() => statsQ.data?.estadisticasRepartos || [], [statsQ.data]);
  const summary = useMemo(() => summarizeRepartos(rows), [rows]);
  const estadosResumen = Object.entries(summary.porEstado)
    .map(([estado, count]) => `${ESTADO_LABELS[estado] || estado}: ${count}`)
    .join(' · ');
  const queryError = statsQ.error?.message || '';

  return (
    <div className="mx-auto w-full max-w-[1400px] space-y-2 px-4 py-1.5" data-qa-screen="repartos-estadisticas">
      <PageHeaderCompact
        title="Estadísticas de repartos"
        description="Entregas, retenciones y efectividad por reparto en el período."
        action={
          <Link
            to="/repartos"
            className="rounded-xl border border-[rgb(var(--border))] px-4 py-2 text-sm font-semibold text-[rgb(var(--fg))] hover:bg-[rgba(var(--muted),0.12)] focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))]"
          >
            Volver a repartos
          </Link>
        }
      />

      <section className="rounded-2xl border border-[rgb(var(--border))] bg-[rgb(var(--panel))] p-2.5">
        <div className="grid grid-cols-12 items-end gap-2.5">
          <label className={`col-span-12 md:col-span-3 ${labelClassName}`}>
            Desde
            <input
              type="date"
              value={desde}
              max={hasta || undefined}
              onChange={event => setRange(prev => ({ ...prev, desde: event.target.value }))}
              className={inputClassName}
            />
          </label>
          <label className={`col-span-12 md:col-span-3 ${labelClassName}`}>
            Hasta
            <input
              type="date"
              value={hasta}
              min={desde || undefined}
              onChange={event => setRange(prev => ({ ...prev, hasta: event.target.value }))}
              className={inputClassName}
            />
          </label>
          <div className="col-span-12 md:col-span-3">
            <button
              type="button"
              onClick={() => setRange(defaultStatsRange())}
              className="w-full rounded-xl border border-[rgb(var(--border))] px-3 py-2 text-sm font-semibold text-[rgb(var(--fg))] hover:bg-[rgba(var(--muted),0.12)] md:w-auto"
            >
              Últimos 30 días
            </button>
          </div>
        </div>
      </section>

      {queryError ? (
        <div className="rounded-2xl border border-[rgba(var(--danger),0.28)] bg-[rgba(var(--danger),0.1)] px-4 py-3 text-sm text-[rgb(var(--danger))]">
          {queryError}
        </div>
      ) : null}

      <section className="grid grid-cols-2 gap-2 md:grid-cols-4 xl:grid-cols-7">
        <KpiCard label="Repartos" value={summary.totalRepartos} hint={estadosResumen || 'Sin repartos'} />
        <KpiCard label="Entregadas" value={summary.entregadas} hint={`de ${summary.totalGuias} guías`} />
        <KpiCard label="Retenidas" value={summary.retenidas} />
        <KpiCard label="No entregadas" value={summary.noEntregadas} />
        <KpiCard label="Pendientes" value={summary.pendientes} hint="Asignadas y reprogramadas" />
        <KpiCard label="Entrega efectiva" value={formatPercent(summary.efectividad)} />
        <KpiCard label="CR pendiente" value={formatCurrency(summary.crPendiente)} />
      </section>

      <section className="overflow-auto rounded-2xl border border-[rgb(var(--border))] bg-[rgb(var(--panel))]" style={{ maxHeight: 'calc(100vh - 380px)' }}>
        <table className="min-w-full text-sm">
          <thead className="sticky top-0 bg-[rgb(var(--subpanel))] text-left text-[11px] uppercase tracking-[0.12em] text-[rgb(var(--muted))]">
            <tr>
              <th className="px-3 py-1.5">Reparto</th>
              <th className="px-3 py-1.5">Fecha</th>
              <th className="px-3 py-1.5">Chofer</th>
              <th className="px-3 py-1.5 text-right">Guías</th>
              <th className="px-3 py-1.5 text-right">Entregadas</th>
              <th className="px-3 py-1.5 text-right">Retenidas</th>
              <th className="px-3 py-1.5 text-right">Pendientes</th>
              <th className="px-3 py-1.5 text-right">% efectividad</th>
              <th className="px-3 py-1.5">Estado</th>
            </tr>
          </thead>
          <tbody>
            {statsQ.fetching ? (
              <tr>
                <td colSpan={9} className="px-3 py-6 text-center text-sm text-[rgb(var(--muted))]">
                  Cargando estadísticas...
                </td>
              </tr>
            ) : null}
            {rows.map(row => (
              <tr key={row.id} className="border-t border-[rgb(var(--border))]">
                <td className="px-3 py-1.5 font-medium">
                  <Link className="text-[rgb(var(--brand))] hover:underline" to={`/repartos/${row.id}`}>
                    #{row.id}
                  </Link>
                </td>
                <td className="px-3 py-1.5">{row.fecha}</td>
                <td className="px-3 py-1.5">{row.chofer_id ? `#${row.chofer_id}` : '-'}</td>
                <td className="px-3 py-1.5 text-right tabular-nums">{row.total_guias}</td>
                <td className="px-3 py-1.5 text-right tabular-nums">{row.entregadas}</td>
                <td className="px-3 py-1.5 text-right tabular-nums">{row.retenidas}</td>
                <td className="px-3 py-1.5 text-right tabular-nums">{pendientesDe(row)}</td>
                <td className="px-3 py-1.5 text-right tabular-nums">{formatPercent(efectividad(row.entregadas, row.total_guias))}</td>
                <td className="px-3 py-1.5">
                  <span className="rounded-full border border-[rgb(var(--border))] px-2 py-0.5 text-[11px]">
                    {ESTADO_LABELS[row.estado] || row.estado}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {!statsQ.fetching && rows.length === 0 ? (
          <div className="p-4">
            <EmptyState title="Sin repartos" description="No hay repartos en el período seleccionado." />
          </div>
        ) : null}
      </section>
    </div>
  );
}
